import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

/**
 * Por dónde va un pedido, y qué sigue.
 *
 * Antes un pedido tenía tres botones —confirmar, marcar entregado, cancelar— y nada más:
 * no había forma de saber si la mercancía estaba empacada o ya iba en camino, ni por qué
 * transportadora, ni con qué guía. Cuando un cliente llamaba a preguntar «¿dónde va mi
 * pedido?», tocaba buscar en el WhatsApp.
 *
 * Aquí está el recorrido completo: lo que ya pasó (con fecha y quién lo hizo) y lo que
 * puede pasar ahora. Cada paso pide lo que de verdad hace falta —la guía si sale, quién
 * recibió si llega— porque esos son los datos que sostienen un reclamo meses después.
 */
export default function SeguimientoPedido({ pedidoId, alCambiar }) {
  const [datos, setDatos] = useState(null);
  const [paso, setPaso] = useState(null);      // el que se está dando ahora
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/pedidos-web/${pedidoId}/seguimiento`);
      setDatos(data);
    } catch (e) { setMsg(e.response?.data?.error || e.message); }
  }, [pedidoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const dar = async () => {
    setGuardando(true); setMsg('');
    try {
      const { data } = await api.put(`/api/pedidos-web/${pedidoId}/estado`,
        { estado: paso.id, ...form });
      setAviso(data.aviso || '');
      setPaso(null); setForm({});
      cargar();
      alCambiar && alCambiar();
    } catch (e) { setMsg(e.response?.data?.error || 'No se pudo'); }
    finally { setGuardando(false); }
  };

  if (!datos) return <div style={s.cargando}>Cargando el seguimiento…</div>;
  const p = datos.pedido;

  // Qué campos pide el paso elegido, dicho como se dice
  const CAMPOS = {
    transportadora: { label: '¿Por dónde sale? *', opciones: datos.transportadoras },
    guia:           { label: 'Número de guía *', ayuda: 'El que el cliente usa para rastrear' },
    fecha_estimada: { label: '¿Para cuándo llega?', tipo: 'date' },
    entregado_a:    { label: '¿Quién lo recibió? *', ayuda: 'Nombre de quien firmó o recibió' },
    entregado_cedula: { label: 'Su cédula' },
    motivo:         { label: 'Motivo *', ayuda: 'Dentro de unos meses nadie se acuerda' },
    envio_cobrado:  { label: 'Envío: ¿cuánto le cobras al cliente?', tipo: 'number',
                      ayuda: 'Escribe 0 si el envío va sin costo para él' },
    envio_costo:    { label: 'Envío: ¿cuánto te cobró la transportadora?', tipo: 'number',
                      ayuda: 'Esto no lo ve el cliente. Es lo que dice si el envío deja o quita' },
  };
  // Los que el paso exige, más los que conviene pedir junto con ellos.
  // El envío se pregunta al despachar porque es cuando de verdad se sabe el valor: antes
  // es una cotización, y después ya nadie vuelve a anotarlo.
  const extras = { enviado: ['fecha_estimada', 'envio_cobrado', 'envio_costo'],
                   entregado: ['entregado_cedula'] };
  const camposDe = (id) => [...(datos.pasos_posibles.find(x => x.id === id)?.exige || []),
                            ...(extras[id] || [])];

  return (
    <div style={s.caja}>
      <div style={s.titulo}>🚚 Seguimiento del pedido</div>

      {/* Dónde está y por dónde va: lo que se le contesta al cliente que llama */}
      <div style={s.ahora}>
        {/* El estado de ahora sale del pedido, no de la última línea del historial: un
            pedido recién llegado no tiene historial y la cabecera mostraba «nuevo» en
            crudo, sin nombre ni icono. */}
        <b style={{ fontSize: '15px' }}>
          {datos.estado_actual?.icono} {datos.estado_actual?.label || p.estado}
        </b>
        {datos.estado_actual?.que_es && (
          <div style={s.queEs}>{datos.estado_actual.que_es}</div>
        )}
        <div style={s.rastreo}>
          {p.transportadora && <span>📮 {p.transportadora}</span>}
          {p.guia && <span style={s.guia}>guía {p.guia}</span>}
          {p.fecha_estimada && (
            <span>llega el {new Date(p.fecha_estimada).toLocaleDateString('es-CO')}</span>
          )}
          {p.entregado_a && (
            <span>recibió <b>{p.entregado_a}</b>
              {p.entregado_cedula ? ` (${p.entregado_cedula})` : ''}</span>
          )}
        </div>
        {p.motivo && <div style={s.motivo}>{p.motivo}</div>}
      </div>

      {/* La plata del pedido, separada.
          «Total» a secas era solo los productos, y desde que se despacha a todo el país
          eso deja por fuera el flete. Mezclarlos sería peor: no se sabría si el envío
          deja algo o se está comiendo la ganancia del producto. */}
      {(p.envio_cobrado != null || p.envio_costo != null) && (
        <div style={s.plata}>
          <div style={s.plataFila}><span>Productos</span><b>{pesos(p.total)}</b></div>
          {p.envio_cobrado != null && (
            <div style={s.plataFila}><span>Envío cobrado al cliente</span>
              <b>{pesos(p.envio_cobrado)}</b></div>
          )}
          <div style={{ ...s.plataFila, ...s.plataTotal }}>
            <span>Total a pagar</span><b>{pesos(p.total_con_envio)}</b></div>
          {p.envio_costo != null && (() => {
            const deja = Number(p.envio_cobrado || 0) - Number(p.envio_costo || 0);
            return (
              <div style={{ ...s.plataFila, ...s.plataInterno }}>
                <span>La transportadora cobró {pesos(p.envio_costo)} · el envío
                  {deja === 0 ? ' sale a cero' : deja > 0 ? ' deja ' : ' quita '}
                  {deja !== 0 && pesos(Math.abs(deja))}</span>
                <b style={{ color: deja < 0 ? '#FCA5A5' : deja > 0 ? '#86EFAC' : 'inherit' }}>
                  {deja < 0 ? '▼' : deja > 0 ? '▲' : '='}
                </b>
              </div>
            );
          })()}
        </div>
      )}

      {aviso && <div style={s.avisoCaja} onClick={() => setAviso('')}>⚠ {aviso}</div>}
      {msg && <div style={s.error} onClick={() => setMsg('')}>{msg}</div>}

      {/* Lo que ya pasó, en orden. Esto es lo que contesta «¿dónde va mi pedido?» */}
      <div style={s.linea}>
        {datos.historial.map((h, i) => (
          <div key={i} style={s.hito}>
            <div style={s.punto}>{h.icono || '•'}</div>
            <div style={{ flex: 1 }}>
              <div style={s.hitoTitulo}>{h.label || h.estado}</div>
              <div style={s.hitoCuando}>
                {new Date(h.creado_en).toLocaleString('es-CO')}
                {h.quien ? ` · ${h.quien}` : ''}
              </div>
              {h.notas && <div style={s.hitoNotas}>{h.notas}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Y lo que puede pasar ahora. Se ofrecen solo los pasos que tienen sentido desde
          donde está: así el estado significa algo y no hay que adivinar cuál sigue. */}
      {!paso && datos.pasos_posibles.length > 0 && (
        <div style={s.botones}>
          {datos.pasos_posibles.map(x => (
            <button key={x.id} type="button" title={x.que_es}
              style={{ ...s.paso, ...(x.id === 'cancelado' ? s.pasoMalo : {}) }}
              onClick={() => { setPaso(x); setForm({}); setAviso(''); }}>
              {x.icono} {x.label}
            </button>
          ))}
        </div>
      )}

      {/* Lo que ese paso pide */}
      {paso && (
        <div style={s.formulario}>
          <div style={s.formTitulo}>{paso.icono} {paso.label}</div>
          <div style={s.formQue}>{paso.que_es}</div>
          {camposDe(paso.id).map(c => {
            const cfg = CAMPOS[c] || { label: c };
            return (
              <div key={c} style={{ marginTop: '9px' }}>
                <label style={s.label}>{cfg.label}</label>
                {cfg.opciones ? (
                  <select style={s.input} value={form[c] || ''}
                    onChange={e => setForm({ ...form, [c]: e.target.value })}>
                    <option value="">— elegir —</option>
                    {cfg.opciones.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={s.input} type={cfg.tipo || 'text'} value={form[c] || ''}
                    onChange={e => setForm({ ...form, [c]: e.target.value })} />
                )}
                {cfg.ayuda && <div style={s.ayuda}>{cfg.ayuda}</div>}
              </div>
            );
          })}
          <div style={{ marginTop: '9px' }}>
            <label style={s.label}>Nota (opcional)</label>
            <input style={s.input} value={form.notas || ''}
              onChange={e => setForm({ ...form, notas: e.target.value })}
              placeholder="Lo que convenga recordar de este paso" />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button type="button" style={s.btn} onClick={() => { setPaso(null); setForm({}); }}>
              Cancelar
            </button>
            <button type="button" style={s.btnP} onClick={dar} disabled={guardando}>
              {guardando ? 'Guardando…' : `Marcar como ${paso.label}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const pesos = (v) => '$' + Math.round(Number(v) || 0).toLocaleString('es-CO');

const s = {
  plata: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.09)',
           borderRadius: '10px', padding: '10px 13px', marginBottom: '12px' },
  plataFila: { display: 'flex', justifyContent: 'space-between', gap: '12px',
               fontSize: '12.5px', color: 'rgba(255,255,255,0.6)', padding: '3px 0' },
  plataTotal: { borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: '4px',
                paddingTop: '7px', color: '#E2E8F0', fontSize: '13.5px' },
  plataInterno: { borderTop: '1px dashed rgba(255,255,255,0.1)', marginTop: '6px',
                  paddingTop: '6px', fontSize: '11.5px', color: 'rgba(255,255,255,0.45)' },
  caja: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '12px', padding: '16px', marginTop: '16px' },
  titulo: { fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.4)', marginBottom: '12px' },
  ahora: { background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.2)',
           borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' },
  queEs: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' },
  rastreo: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '6px',
             fontSize: '12.5px', color: 'rgba(255,255,255,0.6)' },
  guia: { fontFamily: 'monospace', color: '#7DD3FC' },
  motivo: { fontSize: '12.5px', color: '#FDBA74', marginTop: '6px' },
  linea: { display: 'grid', gap: '2px', marginBottom: '14px' },
  hito: { display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '6px 0',
          borderLeft: '2px solid rgba(255,255,255,0.1)', paddingLeft: '12px', marginLeft: '6px' },
  punto: { fontSize: '15px', marginLeft: '-25px', background: '#0F172A', width: '24px',
           textAlign: 'center' },
  hitoTitulo: { fontSize: '13px', color: '#E2E8F0' },
  hitoCuando: { fontSize: '11px', color: 'rgba(255,255,255,0.4)' },
  hitoNotas: { fontSize: '11.5px', color: 'rgba(255,255,255,0.55)', marginTop: '2px',
               fontStyle: 'italic' },
  botones: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  paso: { background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.35)',
          borderRadius: '9px', padding: '9px 14px', color: '#C7D2FE', cursor: 'pointer',
          fontSize: '13px' },
  pasoMalo: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#FCA5A5' },
  formulario: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '10px', padding: '14px', marginTop: '10px' },
  formTitulo: { fontSize: '14px', fontWeight: 600, color: '#E2E8F0' },
  formQue: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' },
  label: { display: 'block', fontSize: '12.5px', color: 'rgba(255,255,255,0.6)',
           marginBottom: '4px' },
  input: { width: '100%', background: 'rgba(15,23,42,0.9)',
           border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
           padding: '9px 11px', color: '#fff', fontSize: '13.5px' },
  ayuda: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '3px' },
  avisoCaja: { background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)',
               borderRadius: '9px', padding: '10px 12px', color: '#FDE047', fontSize: '12.5px',
               marginBottom: '10px', cursor: 'pointer' },
  error: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
           borderRadius: '9px', padding: '10px 12px', color: '#FCA5A5', fontSize: '12.5px',
           marginBottom: '10px', cursor: 'pointer' },
  cargando: { padding: '16px', color: 'rgba(255,255,255,0.4)', fontSize: '13px' },
};
